import { Preferences } from "@capacitor/preferences";
import { Capacitor } from "@capacitor/core";

/**
 * A SMALL VALUE THAT IS STILL THERE TOMORROW MORNING.
 *
 * On device this is `UserDefaults`, which the system does NOT reclaim: it lives in the app's
 * container, iOS never sweeps it under storage pressure, and it goes only when the app does.
 * That is the whole reason this module exists next to `storage.ts` rather than being folded
 * into it.
 *
 * WHY NOT ONE OF THE TWO STORES THAT WERE ALREADY HERE:
 *
 *   - `storage.ts` is the KEYCHAIN. It is for credentials — slow, size-constrained, and
 *     encrypted at a cost you should only pay for a secret. A cached list of rows is not one.
 *   - `localStorage` inside Capacitor is the WKWebView's own store, and WebKit is allowed to
 *     EVICT it when the device is short of space. Nothing breaks when it does; whatever was
 *     kept there is simply gone, silently, and the app quietly goes back to being slow.
 *
 * WHAT THIS IS NOT: private. `UserDefaults` is a plaintext plist inside the container and it
 * is included in iCloud and iTunes backups — exactly as exposed as the web view store it
 * replaces. Durable is the only property gained here. Anything that must not leave the device
 * in a backup, or must not be readable by anyone holding one, belongs in `storage.ts`.
 *
 * On the web it is `localStorage`, and pretending otherwise would be theatre: a browser has
 * no store a page can write that the browser cannot clear.
 *
 * Keys are NOT namespaced by this module. There is no `clear()` to scope, and the callers
 * already carry a prefix of their own (`app.query-cache`), so a second one would only make
 * the key harder to recognise. The plugin adds `CapacitorStorage.` of its own on device, so
 * what is actually on the phone is `CapacitorStorage.app.query-cache`, in
 * `Library/Preferences/<bundle id>.plist` — worth knowing when reading a container dump.
 */
export interface PreferenceStore {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

const isNativeRuntime = Capacitor.isNativePlatform();

export const preferences: PreferenceStore = {
  async get(key) {
    if (isNativeRuntime) {
      const { value } = await Preferences.get({ key });
      return value ?? null;
    }
    try {
      return globalThis.localStorage?.getItem(key) ?? null;
    } catch {
      // Private browsing and a blocked-storage setting throw on ACCESS rather than answering
      // null. Neither is a reason for a caller reading a cached value to fail.
      return null;
    }
  },

  /**
   * A failure here is NOT swallowed, unlike a failed read. Running out of room is something
   * the caller has to know about — the one in this codebase drops its stored copy rather than
   * leaving half of one behind — and a write that silently did nothing would take that
   * decision away from it.
   */
  async set(key, value) {
    if (isNativeRuntime) {
      await Preferences.set({ key, value });
      return;
    }
    globalThis.localStorage?.setItem(key, value);
  },

  async remove(key) {
    if (isNativeRuntime) {
      await Preferences.remove({ key });
      return;
    }
    globalThis.localStorage?.removeItem(key);
  },
};
